"use client";
import { Grid, GridItem } from "@/components/Grid/Grid.component";
import Image from "@/components/Image/Image.component";
import { Typography } from "@/components/Typography/Typography.component";
import { useBreakpoints } from "@/hooks/useBreakpoints/useBreakpoints";
import { changeImagePosition, cn } from "@/lib/utils";
import { COLOR_PICKER } from "@/types/Contentful.model";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { HeroModulePropsModel } from "./HeroModule.model";

gsap.registerPlugin(ScrollTrigger, useGSAP);

const HeroModule = ({
  title,
  image,
  backgroundColor,
}: HeroModulePropsModel) => {
  const [objectPosition, setObjectPosition] = useState("");
  const imgRef = useRef<HTMLImageElement | null>(null);
  const bgColors = [
    "#2050f2",
    "#755c40",
    "#494947",
    "#000000",
    "#ff0000",
    "#161616",
    "#383e4a",
    "#adadad",
  ];
  const cssClass = bgColors.includes(backgroundColor?.value)
    ? "text-core-white"
    : "text-core-black";
  const { isMobile } = useBreakpoints();

  useEffect(() => {
    if (imgRef?.current && isMobile) {
      const { clientWidth: imageWidth, clientHeight: imageHeight } =
        imgRef.current;
      const { width: originalImageWidth, height: originalImageHeight } =
        image?.image || {};
      const { x: originalFocalPointX, y: originalFocalPointY } =
        image?.focalPoint.focalPoint || {};
      if (
        originalImageHeight &&
        originalImageWidth &&
        originalFocalPointX !== undefined &&
        originalFocalPointY !== undefined
      ) {
        const imagePosition = changeImagePosition(
          imageWidth,
          originalImageWidth,
          originalFocalPointX,
          imageHeight,
          originalImageHeight,
          originalFocalPointY,
        );
        setObjectPosition(imagePosition);
        const element = document.getElementById("hero__image");
        if (element) {
          element.style.objectPosition = imagePosition;
        }
      }
    }
  }, [image, imgRef, isMobile]);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add(`(min-width: 768px)`, () => {
      gsap.to("#hero__image", {
        scrollTrigger: {
          trigger: ".hero",
          start: "0px 0px",
          end: "bottom 0px",
          scrub: 1,
        },
        y: -500,
        ease: "none",
        duration: 3,
      });
    });
  }, []);

  return (
    <div
      className={cn(
        `bg-${COLOR_PICKER[backgroundColor.name as keyof typeof COLOR_PICKER]}`,
        `px-sm md:px-md pt-30 pb-20 max-md:overflow-hidden ${image ? "h-[932px] lg:h-[1100px]" : "mb-10 lg:h-auto"} lg:px-lg`,
      )}
    >
      <div className="hero relative">
        <Grid
          className={cn(cssClass, "relative z-10 !p-0 max-md:overflow-hidden")}
        >
          <GridItem span={{ mobile: 4, tablet: 7, desktop: 10 }}>
            <Typography.H1 className="hero__title max-md:!text-(size:--font-size-h3)">
              {title?.split("\n")[0]}
            </Typography.H1>
          </GridItem>
          <GridItem
            span={{ mobile: 3, tablet: 6, desktop: 7 }}
            className="col-start-2 lg:col-start-6"
          >
            <Typography.H1 className="hero__title break-anywhere max-md:!text-(size:--font-size-h3)">
              {title?.split("\n")[1]}
            </Typography.H1>
          </GridItem>
        </Grid>
        {image && (
          <Image
            ref={imgRef}
            id="hero__image"
            src={`${image.image.url}?fm=webp&w=${isMobile ? 430 : 636}`}
            alt={image.image.title || title}
            width={isMobile ? 430 : 636}
            height={isMobile ? 538 : 795}
            style={{ objectPosition: objectPosition }}
            className="relative bottom-4 left-1/2 col-span-12 -translate-x-1/2 transform lg:bottom-8"
            hasPlaceholder
          />
        )}
      </div>
    </div>
  );
};

export default HeroModule;
